import { z } from "zod";
import { EntitlementKind, IsoDateTime, OrderStatus, paged, PageQuery, Reason, Uuid } from "./common.ts";
import { actionsOf } from "./permissions.ts";

/*
 * Orders (07 §2.2, UC-03, UC-04, UC-23, 06 `order`). An order is one product (ADR-029) and a
 * record with a status even when the team handles it by hand (ADR-009). The entitlement
 * comes with `completed`, never with the payment (R-09).
 */

/** The product as the order shows it; texts come from dataCycle. */
export const OrderProduct = z.object({
  id: Uuid,
  slug: z.string().min(1),
  name: z.string().min(1),
});

export const Order = z.object({
  id: Uuid,
  customerId: Uuid,
  product: OrderProduct,
  /**
   * `demo` for a demo requested through `POST /api/entitlements/demo` (UC-06), `standard` for an
   * order (UC-03); the entitlement the order leads to gets the same kind (ADR-029)
   */
  kind: EntitlementKind,
  /** the user who placed it; null when the team created it */
  placedBy: Uuid.nullable(),
  status: OrderStatus,
  /** invoice number from outside the platform (ADR-007), set by BackOffice (UC-04) */
  invoiceReference: z.string().nullable(),
  cancelledReason: z.string().nullable(),
  completedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  /**
   * ADR-075, UC-23: `order.cancel` for the customer's administrator while the order is
   * `received` without invoice reference; otherwise `order_in_progress`, `order_invoiced` or
   * `order_closed` (TL, 2026-10-14)
   */
  actions: actionsOf("order.cancel"),
});
export type Order = z.infer<typeof Order>;

/**
 * `POST /api/orders` (UC-03), answered with `201` and the order. Ordering needs no approval
 * (R-02). A product the catalogue does not show is `404`. Rejected with `forbidden` and reason
 * `role_required` or `customer_suspended` for the actor, with `conflict` and reason
 * `inquiry_only` for a product on request (ADR-112, before every other reason at the product),
 * `demo_only` for a product with billing model demo (ADR-082 point 4), `entitlement_exists` or `order_open`
 * when a living entitlement or an open order for the product exists (R-01, ADR-029). A demo is
 * requested through `POST /api/entitlements/demo`.
 */
export const OrderCreateRequest = z.object({
  productId: Uuid,
});
export type OrderCreateRequest = z.infer<typeof OrderCreateRequest>;

/** `GET /api/orders`, newest first. */
export const OrderListQuery = PageQuery.extend({
  status: OrderStatus.optional(),
});
export type OrderListQuery = z.infer<typeof OrderListQuery>;

export const OrderListResponse = paged(Order);
export type OrderListResponse = z.infer<typeof OrderListResponse>;

/**
 * `POST /api/orders/:id/cancel` (UC-23), answered with the order: the customer's administrator
 * cancels while the order is `received` and carries no invoice reference; once staff work on it
 * or it is invoiced, only staff cancel (TL, 2026-10-14). The reason is optional here, staff must
 * give one.
 */
export const OrderCancelRequest = z.object({
  reason: Reason.optional(),
});
export type OrderCancelRequest = z.infer<typeof OrderCancelRequest>;
